import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import examBoardService from '../../services/examBoardService';
import studentsService from '../../services/studentsService';
import staffService from '../../services/staffService';
import classesService from '../../services/classesService';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, Plus, PencilSimple, Trash, DownloadSimple, Armchair, Users, UploadSimple, ClipboardText, ChartBar, CurrencyCircleDollar } from '@phosphor-icons/react';

const STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'index_issued', label: 'Index Issued' },
  { value: 'results_received', label: 'Results Received' },
  { value: 'cancelled', label: 'Cancelled' },
];

const emptyForm = {
  student_id: '',
  exam_name: 'BECE',
  exam_year: new Date().getFullYear().toString(),
  index_number: '',
  subject_ids: [],
  registration_status: 'pending',
  registration_fee_amount: '',
  exam_center: '',
  notes: '',
};

const emptyBulkForm = {
  class_id: '',
  student_ids: [],
  exam_name: 'BECE',
  exam_year: new Date().getFullYear().toString(),
  subject_ids: [],
  registration_fee_amount: '',
  exam_center: '',
  notes: '',
};

function ExamBoardRegistrationsPage() {
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [staff, setStaff] = useState([]);
  const [seating, setSeating] = useState([]);
  const [invigilationDuties, setInvigilationDuties] = useState([]);
  const [seatRegistrationId, setSeatRegistrationId] = useState('');
  const [seatRoom, setSeatRoom] = useState('');
  const [seatNumber, setSeatNumber] = useState('');
  const [assigningSeat, setAssigningSeat] = useState(false);
  const [dutyStaffId, setDutyStaffId] = useState('');
  const [dutyRoom, setDutyRoom] = useState('');
  const [dutyDate, setDutyDate] = useState(new Date().toISOString().split('T')[0]);
  const [dutyExamName, setDutyExamName] = useState('BECE');
  const [dutyExamYear, setDutyExamYear] = useState(new Date().getFullYear().toString());
  const [assigningDuty, setAssigningDuty] = useState(false);

  const [classSubjects, setClassSubjects] = useState([]);
  const [classes, setClasses] = useState([]);

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState(emptyBulkForm);
  const [bulkClassStudents, setBulkClassStudents] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const [showIndexImportModal, setShowIndexImportModal] = useState(false);
  const [indexImportForm, setIndexImportForm] = useState({ exam_name: 'BECE', exam_year: new Date().getFullYear().toString(), csv_text: '' });
  const [indexImportSubmitting, setIndexImportSubmitting] = useState(false);

  const [showResultsImportModal, setShowResultsImportModal] = useState(false);
  const [resultsImportForm, setResultsImportForm] = useState({ exam_name: 'BECE', exam_year: new Date().getFullYear().toString(), csv_text: '' });
  const [resultsImportSubmitting, setResultsImportSubmitting] = useState(false);

  const [payFeeTarget, setPayFeeTarget] = useState(null);
  const [payFeeAmount, setPayFeeAmount] = useState('');
  const [payFeeMethod, setPayFeeMethod] = useState('cash');
  const [payingFee, setPayingFee] = useState(false);

  const [resultsTarget, setResultsTarget] = useState(null);
  const [resultsSubjects, setResultsSubjects] = useState([]);
  const [resultsForm, setResultsForm] = useState({});
  const [savingResults, setSavingResults] = useState(false);

  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let filtered = registrations;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((r) => getStudentName(r.student_id).toLowerCase().includes(term));
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.registration_status === statusFilter);
    }
    setFilteredRegistrations(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations, searchTerm, statusFilter, students]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [regsData, studentsData, staffData, seatingData, dutiesData, classesData] = await Promise.all([
        examBoardService.listRegistrations(),
        studentsService.listStudents().catch(() => []),
        staffService.listStaff().catch(() => []),
        examBoardService.listSeating().catch(() => []),
        examBoardService.listInvigilationDuties().catch(() => []),
        classesService.listClasses().catch(() => []),
      ]);
      setRegistrations(Array.isArray(regsData) ? regsData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : studentsData?.items || studentsData?.students || []);
      setStaff(Array.isArray(staffData) ? staffData : staffData?.items || staffData?.staff || []);
      setSeating(Array.isArray(seatingData) ? seatingData : []);
      setInvigilationDuties(Array.isArray(dutiesData) ? dutiesData : []);
      setClasses(Array.isArray(classesData) ? classesData : classesData?.items || []);
    } catch (error) {
      toast.error('Failed to load exam board registrations');
    } finally {
      setLoading(false);
    }
  };

  const getStaffName = (staffId) => {
    const member = staff.find((s) => s.id === staffId);
    return member ? `${member.first_name} ${member.last_name}` : staffId;
  };

  const handleAssignSeat = async () => {
    if (!seatRegistrationId || !seatRoom || !seatNumber) {
      toast.error('Select a registration and enter room + seat number');
      return;
    }
    setAssigningSeat(true);
    try {
      await examBoardService.assignSeating({
        registration_id: seatRegistrationId,
        room: seatRoom,
        seat_number: seatNumber,
      });
      toast.success('Seat assigned');
      setSeatRegistrationId('');
      setSeatRoom('');
      setSeatNumber('');
      const seatingData = await examBoardService.listSeating();
      setSeating(Array.isArray(seatingData) ? seatingData : []);
    } catch (error) {
      toast.error(error.message || 'Failed to assign seat');
    } finally {
      setAssigningSeat(false);
    }
  };

  const handleDeleteSeat = async (assignmentId) => {
    try {
      await examBoardService.deleteSeating(assignmentId);
      toast.success('Seat assignment removed');
      const seatingData = await examBoardService.listSeating();
      setSeating(Array.isArray(seatingData) ? seatingData : []);
    } catch (error) {
      toast.error('Failed to remove seat assignment');
    }
  };

  const handleAssignDuty = async () => {
    if (!dutyStaffId || !dutyRoom || !dutyDate || !dutyExamName || !dutyExamYear) {
      toast.error('Fill in all invigilation duty fields');
      return;
    }
    setAssigningDuty(true);
    try {
      await examBoardService.createInvigilationDuty({
        staff_id: dutyStaffId,
        room: dutyRoom,
        duty_date: dutyDate,
        exam_name: dutyExamName,
        exam_year: dutyExamYear,
      });
      toast.success('Invigilation duty assigned');
      setDutyRoom('');
      const dutiesData = await examBoardService.listInvigilationDuties();
      setInvigilationDuties(Array.isArray(dutiesData) ? dutiesData : []);
    } catch (error) {
      toast.error(error.message || 'Failed to assign invigilation duty');
    } finally {
      setAssigningDuty(false);
    }
  };

  const handleDeleteDuty = async (dutyId) => {
    try {
      await examBoardService.deleteInvigilationDuty(dutyId);
      toast.success('Invigilation duty removed');
      const dutiesData = await examBoardService.listInvigilationDuties();
      setInvigilationDuties(Array.isArray(dutiesData) ? dutiesData : []);
    } catch (error) {
      toast.error('Failed to remove invigilation duty');
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find((s) => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : studentId;
  };

  const loadSubjectsForStudent = async (studentId) => {
    const student = students.find((s) => s.id === studentId);
    if (!student || !student.class_id) { setClassSubjects([]); return; }
    try {
      const cls = await classesService.getClass(student.class_id);
      setClassSubjects(cls.class_subjects || []);
    } catch (error) {
      setClassSubjects([]);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setClassSubjects([]);
    setShowModal(true);
  };

  const openEditModal = async (reg) => {
    setEditingId(reg.id);
    let subjectIds = [];
    try {
      const subs = await examBoardService.getRegistrationSubjects(reg.id);
      subjectIds = (subs || []).map((s) => s.subject_id);
    } catch (error) { /* leave empty if this fails */ }
    setFormData({
      student_id: reg.student_id,
      exam_name: reg.exam_name,
      exam_year: reg.exam_year,
      index_number: reg.index_number || '',
      subject_ids: subjectIds,
      registration_status: reg.registration_status,
      registration_fee_amount: reg.registration_fee_amount || '',
      exam_center: reg.exam_center || '',
      notes: reg.notes || '',
    });
    await loadSubjectsForStudent(reg.student_id);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student_id || !formData.exam_name || !formData.exam_year) {
      toast.error('Student, exam name, and exam year are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        registration_fee_amount: formData.registration_fee_amount ? Number(formData.registration_fee_amount) : null,
      };
      if (editingId) {
        const { student_id, ...rest } = payload;
        await examBoardService.updateRegistration(editingId, rest);
        toast.success('Registration updated');
      } else {
        await examBoardService.createRegistration(payload);
        toast.success('Registration created');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save registration');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await examBoardService.deleteRegistration(deleteTarget);
      toast.success('Registration deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete registration');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDownloadHallTicket = async (reg) => {
    setDownloadingId(reg.id);
    try {
      const response = await examBoardService.downloadHallTicket(reg.id);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hall_ticket_${getStudentName(reg.student_id).replace(/\s+/g, '_')}_${reg.exam_name}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      toast.error(error.message || 'Failed to download hall ticket');
    } finally {
      setDownloadingId(null);
    }
  };

  // ---- Bulk cohort registration ----

  const openBulkModal = () => {
    setBulkForm(emptyBulkForm);
    setBulkClassStudents([]);
    setShowBulkModal(true);
  };

  const handleBulkClassChange = async (classId) => {
    setBulkForm((f) => ({ ...f, class_id: classId, student_ids: [] }));
    if (!classId) { setBulkClassStudents([]); return; }
    const inClass = students.filter((s) => s.class_id === classId);
    setBulkClassStudents(inClass);
    if (inClass.length > 0) {
      try {
        const cls = await classesService.getClass(classId);
        setClassSubjects(cls.class_subjects || []);
      } catch (error) { setClassSubjects([]); }
    }
  };

  const toggleBulkStudent = (studentId) => {
    setBulkForm((f) => ({
      ...f,
      student_ids: f.student_ids.includes(studentId)
        ? f.student_ids.filter((id) => id !== studentId)
        : [...f.student_ids, studentId],
    }));
  };

  const toggleAllBulkStudents = () => {
    setBulkForm((f) => ({
      ...f,
      student_ids: f.student_ids.length === bulkClassStudents.length ? [] : bulkClassStudents.map((s) => s.id),
    }));
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (bulkForm.student_ids.length === 0 || !bulkForm.exam_name || !bulkForm.exam_year) {
      toast.error('Select at least one student, and fill in exam name/year');
      return;
    }
    setBulkSubmitting(true);
    try {
      const result = await examBoardService.bulkCreateRegistrations({
        student_ids: bulkForm.student_ids,
        exam_name: bulkForm.exam_name,
        exam_year: bulkForm.exam_year,
        subject_ids: bulkForm.subject_ids,
        registration_fee_amount: bulkForm.registration_fee_amount ? Number(bulkForm.registration_fee_amount) : null,
        exam_center: bulkForm.exam_center || null,
        notes: bulkForm.notes || null,
      });
      toast.success(`Registered ${result.created_count} student(s)${result.skipped_count ? `, skipped ${result.skipped_count}` : ''}`);
      setShowBulkModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Bulk registration failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  // ---- Bulk index-number import ----

  const handleIndexImportSubmit = async (e) => {
    e.preventDefault();
    if (!indexImportForm.exam_name || !indexImportForm.exam_year || !indexImportForm.csv_text.trim()) {
      toast.error('Fill in exam name, year, and the CSV data');
      return;
    }
    setIndexImportSubmitting(true);
    try {
      const result = await examBoardService.bulkImportIndexNumbers(indexImportForm);
      toast.success(`Updated ${result.updated_count} registration(s)${result.not_found_count ? `, ${result.not_found_count} not matched` : ''}`);
      setShowIndexImportModal(false);
      setIndexImportForm({ exam_name: 'BECE', exam_year: new Date().getFullYear().toString(), csv_text: '' });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Index number import failed');
    } finally {
      setIndexImportSubmitting(false);
    }
  };

  // ---- Bulk results import ----

  const handleResultsImportSubmit = async (e) => {
    e.preventDefault();
    if (!resultsImportForm.exam_name || !resultsImportForm.exam_year || !resultsImportForm.csv_text.trim()) {
      toast.error('Fill in exam name, year, and the CSV data');
      return;
    }
    setResultsImportSubmitting(true);
    try {
      const result = await examBoardService.bulkImportResults(resultsImportForm);
      toast.success(`Recorded ${result.updated_count} result(s)${result.not_found_count ? `, ${result.not_found_count} not matched` : ''}`);
      setShowResultsImportModal(false);
      setResultsImportForm({ exam_name: 'BECE', exam_year: new Date().getFullYear().toString(), csv_text: '' });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Results import failed');
    } finally {
      setResultsImportSubmitting(false);
    }
  };

  // ---- Fee payment ----

  const openPayFeeModal = (reg) => {
    setPayFeeTarget(reg);
    setPayFeeAmount('');
    setPayFeeMethod('cash');
  };

  const handlePayFee = async () => {
    if (!payFeeTarget) return;
    setPayingFee(true);
    try {
      const result = await examBoardService.payRegistrationFee(payFeeTarget.id, {
        amount: payFeeAmount ? Number(payFeeAmount) : null,
        payment_method: payFeeMethod,
      });
      toast.success(result.fully_paid ? 'Fee fully paid' : `Payment recorded — GHS ${result.balance.toFixed(2)} still owed`);
      setPayFeeTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to record payment');
    } finally {
      setPayingFee(false);
    }
  };

  // ---- Results entry (per registration) ----

  const openResultsModal = async (reg) => {
    setResultsTarget(reg);
    try {
      const [subjects, results] = await Promise.all([
        examBoardService.getRegistrationSubjects(reg.id),
        examBoardService.getResults(reg.id),
      ]);
      setResultsSubjects(subjects || []);
      const form = {};
      (subjects || []).forEach((s) => {
        const existing = (results || []).find((r) => r.subject_id === s.subject_id);
        form[s.subject_id] = { grade: existing?.grade || '', score: existing?.score ?? '', remarks: existing?.remarks || '' };
      });
      setResultsForm(form);
    } catch (error) {
      toast.error('Failed to load subjects/results for this registration');
      setResultsSubjects([]);
      setResultsForm({});
    }
  };

  const handleSaveResults = async () => {
    if (!resultsTarget) return;
    setSavingResults(true);
    try {
      const results = resultsSubjects.map((s) => ({
        subject_id: s.subject_id,
        grade: resultsForm[s.subject_id]?.grade || null,
        score: resultsForm[s.subject_id]?.score !== '' ? Number(resultsForm[s.subject_id]?.score) : null,
        remarks: resultsForm[s.subject_id]?.remarks || null,
      }));
      await examBoardService.submitResults(resultsTarget.id, results);
      toast.success('Results saved');
      setResultsTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save results');
    } finally {
      setSavingResults(false);
    }
  };

  // ---- Analytics ----

  const toggleAnalytics = async () => {
    if (!showAnalytics) {
      try {
        const data = await examBoardService.getAnalytics();
        setAnalytics(data);
      } catch (error) {
        toast.error('Failed to load analytics');
      }
    }
    setShowAnalytics((v) => !v);
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading exam board registrations...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Exam Board — Registrations</h1>
            <p>Register students for external exams (BECE, WASSCE) and download hall tickets</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={toggleAnalytics}>
              <ChartBar size={18} />
              {showAnalytics ? 'Hide Analytics' : 'Analytics'}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowIndexImportModal(true)}>
              <UploadSimple size={18} />
              Import Index Numbers
            </button>
            <button className="btn btn-secondary" onClick={() => setShowResultsImportModal(true)}>
              <ClipboardText size={18} />
              Import Results
            </button>
            <button className="btn btn-secondary" onClick={openBulkModal}>
              <Users size={18} />
              Bulk Register
            </button>
            <button className="btn btn-primary" onClick={openCreateModal}>
              <Plus size={20} />
              Register Student
            </button>
          </div>
        </div>

        {showAnalytics && analytics && (
          <div className="form-card" style={{ marginBottom: 20, padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Analytics — All Sittings</h3>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div><strong>{analytics.total_registrations}</strong><div style={{ fontSize: 12, color: '#6b7280' }}>Total registrations</div></div>
              {Object.entries(analytics.by_status).map(([status, count]) => (
                <div key={status}><strong>{count}</strong><div style={{ fontSize: 12, color: '#6b7280', textTransform: 'capitalize' }}>{status.replace('_', ' ')}</div></div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
              <div><strong>GHS {analytics.fee.total_owed.toFixed(2)}</strong><div style={{ fontSize: 12, color: '#6b7280' }}>Total owed to board</div></div>
              <div><strong>GHS {analytics.fee.total_collected.toFixed(2)}</strong><div style={{ fontSize: 12, color: '#6b7280' }}>Collected</div></div>
              <div><strong>GHS {analytics.fee.outstanding.toFixed(2)}</strong><div style={{ fontSize: 12, color: '#9A3412' }}>Outstanding</div></div>
            </div>
            {analytics.results.subjects_with_results > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Grade distribution ({analytics.results.subjects_with_results} subject results)</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {Object.entries(analytics.results.grade_distribution).map(([grade, count]) => (
                    <div key={grade} style={{ fontSize: 13 }}><strong>{grade}:</strong> {count}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search by student name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
            <option value="all">All Statuses</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="table-container">
          {filteredRegistrations.length === 0 ? (
            <div className="empty-state">
              <p>No registrations found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Exam</th>
                  <th>Year</th>
                  <th>Index Number</th>
                  <th>Status</th>
                  <th>Fee Paid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRegistrations.map((reg) => (
                  <tr key={reg.id}>
                    <td>{getStudentName(reg.student_id)}</td>
                    <td>{reg.exam_name}</td>
                    <td>{reg.exam_year}</td>
                    <td>{reg.index_number || '-'}</td>
                    <td>{STATUSES.find((s) => s.value === reg.registration_status)?.label}</td>
                    <td>
                      {!reg.registration_fee_amount ? '-' : reg.registration_fee_paid ? 'Paid' : (
                        <span style={{ color: '#9A3412' }}>Owing GHS {reg.registration_fee_amount.toFixed(2)}</span>
                      )}
                    </td>
                    <td className="action-buttons">
                      {reg.fee_id && !reg.registration_fee_paid && (
                        <button className="btn-icon btn-primary" title="Pay Fee" onClick={() => openPayFeeModal(reg)}>
                          <CurrencyCircleDollar size={18} />
                        </button>
                      )}
                      <button className="btn-icon" title="Enter Results" onClick={() => openResultsModal(reg)}>
                        <ClipboardText size={18} />
                      </button>
                      <button
                        className="btn-icon btn-primary"
                        title="Download Hall Ticket"
                        onClick={() => handleDownloadHallTicket(reg)}
                        disabled={downloadingId === reg.id}
                      >
                        <DownloadSimple size={18} />
                      </button>
                      <button className="btn-icon" title="Edit" onClick={() => openEditModal(reg)}>
                        <PencilSimple size={18} />
                      </button>
                      <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteTarget(reg.id)}>
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="form-card" style={{ marginTop: 24, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Hall Seating</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0, minWidth: 220 }}>
              <label>Registration</label>
              <select value={seatRegistrationId} onChange={(e) => setSeatRegistrationId(e.target.value)}>
                <option value="">Select Registration</option>
                {registrations
                  .filter((r) => !seating.some((s) => s.registration_id === r.id))
                  .map((r) => (
                    <option key={r.id} value={r.id}>{getStudentName(r.student_id)} — {r.exam_name} {r.exam_year}</option>
                  ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Room</label>
              <input type="text" value={seatRoom} onChange={(e) => setSeatRoom(e.target.value)} placeholder="e.g., Hall A" style={{ width: 120 }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Seat Number</label>
              <input type="text" value={seatNumber} onChange={(e) => setSeatNumber(e.target.value)} placeholder="e.g., 14" style={{ width: 100 }} />
            </div>
            <button className="btn btn-primary" onClick={handleAssignSeat} disabled={assigningSeat}>
              <Armchair size={18} />
              {assigningSeat ? 'Assigning...' : 'Assign Seat'}
            </button>
          </div>
          {seating.length > 0 && (
            <table className="data-table">
              <thead>
                <tr><th>Student</th><th>Exam</th><th>Room</th><th>Seat</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {seating.map((s) => {
                  const reg = registrations.find((r) => r.id === s.registration_id);
                  return (
                    <tr key={s.id}>
                      <td>{reg ? getStudentName(reg.student_id) : '-'}</td>
                      <td>{reg ? `${reg.exam_name} ${reg.exam_year}` : '-'}</td>
                      <td>{s.room}</td>
                      <td>{s.seat_number}</td>
                      <td className="action-buttons">
                        <button className="btn-icon btn-danger" title="Remove" onClick={() => handleDeleteSeat(s.id)}>
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="form-card" style={{ marginTop: 20, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Invigilation Duties</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
              <label>Staff</label>
              <select value={dutyStaffId} onChange={(e) => setDutyStaffId(e.target.value)}>
                <option value="">Select Staff</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Exam</label>
              <input type="text" value={dutyExamName} onChange={(e) => setDutyExamName(e.target.value)} style={{ width: 100 }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Year</label>
              <input type="text" value={dutyExamYear} onChange={(e) => setDutyExamYear(e.target.value)} style={{ width: 80 }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Room</label>
              <input type="text" value={dutyRoom} onChange={(e) => setDutyRoom(e.target.value)} placeholder="e.g., Hall A" style={{ width: 120 }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Date</label>
              <input type="date" value={dutyDate} onChange={(e) => setDutyDate(e.target.value)} />
            </div>
            <button className="btn btn-primary" onClick={handleAssignDuty} disabled={assigningDuty}>
              <Plus size={18} />
              {assigningDuty ? 'Assigning...' : 'Assign Duty'}
            </button>
          </div>
          {invigilationDuties.length > 0 && (
            <table className="data-table">
              <thead>
                <tr><th>Staff</th><th>Exam</th><th>Room</th><th>Date</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {invigilationDuties.map((d) => (
                  <tr key={d.id}>
                    <td>{getStaffName(d.staff_id)}</td>
                    <td>{d.exam_name} {d.exam_year}</td>
                    <td>{d.room}</td>
                    <td>{d.duty_date}</td>
                    <td className="action-buttons">
                      <button className="btn-icon btn-danger" title="Remove" onClick={() => handleDeleteDuty(d.id)}>
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Registration' : 'Register Student'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {!editingId && (
                <div className="form-group">
                  <label>Student *</label>
                  <select
                    value={formData.student_id}
                    onChange={(e) => { setFormData({ ...formData, student_id: e.target.value, subject_ids: [] }); loadSubjectsForStudent(e.target.value); }}
                    required
                  >
                    <option value="">Select Student</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Exam Name *</label>
                  <input type="text" value={formData.exam_name} onChange={(e) => setFormData({ ...formData, exam_name: e.target.value })} placeholder="e.g., BECE, WASSCE" required />
                </div>
                <div className="form-group">
                  <label>Exam Year *</label>
                  <input type="text" value={formData.exam_year} onChange={(e) => setFormData({ ...formData, exam_year: e.target.value })} required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Index Number</label>
                  <input type="text" value={formData.index_number} onChange={(e) => setFormData({ ...formData, index_number: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select value={formData.registration_status} onChange={(e) => setFormData({ ...formData, registration_status: e.target.value })} required>
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Subjects Registered</label>
                {classSubjects.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {formData.student_id ? "This student's class has no subjects assigned yet." : 'Select a student to see their class subjects.'}
                  </p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6 }}>
                    {classSubjects.map((cs) => (
                      <label key={cs.subject_id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={formData.subject_ids.includes(cs.subject_id)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...formData.subject_ids, cs.subject_id]
                              : formData.subject_ids.filter((id) => id !== cs.subject_id);
                            setFormData({ ...formData, subject_ids: next });
                          }}
                          style={{ width: 'auto' }}
                        />
                        {cs.subject_name}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Exam Center</label>
                <input type="text" value={formData.exam_center} onChange={(e) => setFormData({ ...formData, exam_center: e.target.value })} />
              </div>

              <div className="form-group">
                <label>Registration Fee (GHS)</label>
                <input type="number" step="0.01" value={formData.registration_fee_amount} onChange={(e) => setFormData({ ...formData, registration_fee_amount: e.target.value })} />
                <small style={{ color: 'var(--text-secondary)' }}>Use the Pay Fee action on the table to record a payment once registered.</small>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Register'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Register Modal */}
      {showBulkModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>Bulk Register a Class</h2>
              <button type="button" className="btn-close" onClick={() => setShowBulkModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleBulkSubmit}>
              <div className="form-group">
                <label>Class *</label>
                <select value={bulkForm.class_id} onChange={(e) => handleBulkClassChange(e.target.value)} required>
                  <option value="">Select Class</option>
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {bulkClassStudents.length > 0 && (
                <div className="form-group">
                  <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Students ({bulkForm.student_ids.length} of {bulkClassStudents.length} selected)</span>
                    <button type="button" className="btn btn-sm" onClick={toggleAllBulkStudents}>
                      {bulkForm.student_ids.length === bulkClassStudents.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6, maxHeight: 180, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                    {bulkClassStudents.map((s) => (
                      <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal' }}>
                        <input type="checkbox" checked={bulkForm.student_ids.includes(s.id)} onChange={() => toggleBulkStudent(s.id)} style={{ width: 'auto' }} />
                        {s.first_name} {s.last_name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Exam Name *</label>
                  <input type="text" value={bulkForm.exam_name} onChange={(e) => setBulkForm({ ...bulkForm, exam_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Exam Year *</label>
                  <input type="text" value={bulkForm.exam_year} onChange={(e) => setBulkForm({ ...bulkForm, exam_year: e.target.value })} required />
                </div>
              </div>

              {classSubjects.length > 0 && (
                <div className="form-group">
                  <label>Subjects (applied to every selected student)</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6 }}>
                    {classSubjects.map((cs) => (
                      <label key={cs.subject_id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={bulkForm.subject_ids.includes(cs.subject_id)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...bulkForm.subject_ids, cs.subject_id]
                              : bulkForm.subject_ids.filter((id) => id !== cs.subject_id);
                            setBulkForm({ ...bulkForm, subject_ids: next });
                          }}
                          style={{ width: 'auto' }}
                        />
                        {cs.subject_name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Exam Center</label>
                  <input type="text" value={bulkForm.exam_center} onChange={(e) => setBulkForm({ ...bulkForm, exam_center: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Registration Fee (GHS, per student)</label>
                  <input type="number" step="0.01" value={bulkForm.registration_fee_amount} onChange={(e) => setBulkForm({ ...bulkForm, registration_fee_amount: e.target.value })} />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={bulkSubmitting}>
                  {bulkSubmitting ? 'Registering...' : `Register ${bulkForm.student_ids.length || ''} Student(s)`}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowBulkModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Index Number Bulk Import Modal */}
      {showIndexImportModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Import Index Numbers</h2>
              <button type="button" className="btn-close" onClick={() => setShowIndexImportModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleIndexImportSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Exam Name *</label>
                  <input type="text" value={indexImportForm.exam_name} onChange={(e) => setIndexImportForm({ ...indexImportForm, exam_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Exam Year *</label>
                  <input type="text" value={indexImportForm.exam_year} onChange={(e) => setIndexImportForm({ ...indexImportForm, exam_year: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label>CSV Data *</label>
                <textarea
                  rows={8}
                  placeholder={'student_id,index_number\nTPS2024001,4012345678\nTPS2024002,4012345679'}
                  value={indexImportForm.csv_text}
                  onChange={(e) => setIndexImportForm({ ...indexImportForm, csv_text: e.target.value })}
                  required
                />
                <small style={{ color: 'var(--text-secondary)' }}>Two columns: the school's student ID, then the index number the board issued. Paste directly from a spreadsheet.</small>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={indexImportSubmitting}>
                  {indexImportSubmitting ? 'Importing...' : 'Import'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowIndexImportModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Results Bulk Import Modal */}
      {showResultsImportModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Import Results</h2>
              <button type="button" className="btn-close" onClick={() => setShowResultsImportModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleResultsImportSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Exam Name *</label>
                  <input type="text" value={resultsImportForm.exam_name} onChange={(e) => setResultsImportForm({ ...resultsImportForm, exam_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Exam Year *</label>
                  <input type="text" value={resultsImportForm.exam_year} onChange={(e) => setResultsImportForm({ ...resultsImportForm, exam_year: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label>CSV Data *</label>
                <textarea
                  rows={8}
                  placeholder={'student_id,subject_code,grade,score\nTPS2024001,MATH,A1,85\nTPS2024001,ENG,B2,72'}
                  value={resultsImportForm.csv_text}
                  onChange={(e) => setResultsImportForm({ ...resultsImportForm, csv_text: e.target.value })}
                  required
                />
                <small style={{ color: 'var(--text-secondary)' }}>Four columns: student ID, subject code, grade, score. Paste directly from the board's results spreadsheet.</small>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={resultsImportSubmitting}>
                  {resultsImportSubmitting ? 'Importing...' : 'Import'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowResultsImportModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pay Fee Modal */}
      {payFeeTarget && (
        <div className="modal-overlay" onClick={() => setPayFeeTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Pay Registration Fee</h2>
              <button type="button" className="btn-close" onClick={() => setPayFeeTarget(null)} aria-label="Close">×</button>
            </div>
            <p>{getStudentName(payFeeTarget.student_id)} — {payFeeTarget.exam_name} {payFeeTarget.exam_year}</p>
            <div className="form-group">
              <label>Amount (GHS) — leave blank to pay the full outstanding balance</label>
              <input type="number" step="0.01" value={payFeeAmount} onChange={(e) => setPayFeeAmount(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Payment Method</label>
              <select value={payFeeMethod} onChange={(e) => setPayFeeMethod(e.target.value)}>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-primary" onClick={handlePayFee} disabled={payingFee}>
                {payingFee ? 'Recording...' : 'Record Payment'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setPayFeeTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Results Entry Modal */}
      {resultsTarget && (
        <div className="modal-overlay" onClick={() => setResultsTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>Enter Results</h2>
              <button type="button" className="btn-close" onClick={() => setResultsTarget(null)} aria-label="Close">×</button>
            </div>
            <p>{getStudentName(resultsTarget.student_id)} — {resultsTarget.exam_name} {resultsTarget.exam_year}</p>
            {resultsSubjects.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>This registration has no linked subjects to record results for. Edit the registration to select subjects first.</p>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {resultsSubjects.map((s) => (
                  <div key={s.subject_id} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 13 }}>{s.subject_name}</span>
                    <input
                      type="text" placeholder="Grade" value={resultsForm[s.subject_id]?.grade || ''}
                      onChange={(e) => setResultsForm({ ...resultsForm, [s.subject_id]: { ...resultsForm[s.subject_id], grade: e.target.value } })}
                    />
                    <input
                      type="number" step="0.01" placeholder="Score" value={resultsForm[s.subject_id]?.score ?? ''}
                      onChange={(e) => setResultsForm({ ...resultsForm, [s.subject_id]: { ...resultsForm[s.subject_id], score: e.target.value } })}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="form-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-primary" onClick={handleSaveResults} disabled={savingResults || resultsSubjects.length === 0}>
                {savingResults ? 'Saving...' : 'Save Results'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setResultsTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Registration"
        message="This will permanently delete this exam registration. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default ExamBoardRegistrationsPage;
